import * as React from 'react'
import { getCookie, removeCookie, setCookie } from '../lib/cookies'
import { sleep } from '@/lib/utils'
import { jwtVerify } from 'jose'
import { DiscordUser } from '@/types/discord'
import { useNavigate } from '@tanstack/react-router'

export interface AuthContext {
  isAuthenticated: boolean
  login: (user: any, token: string) => Promise<void>
  logout: () => Promise<void>
  verify: () => Promise<void>
  checkPermissions: () => Promise<boolean | null>
  loading: boolean
  user: DiscordUser | null
}

const AuthContext = React.createContext<AuthContext | null>(null)

const key = 'token'

async function getDataFromToken() {
  try {
    const token = getCookie("token");
    if (!token) return null;

    const respVerify = await fetch("/api/jwt/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ token })
    })

    const payload = await respVerify.json();

    const resp = await fetch("/api/me", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ id: payload.data })
    })

    const data = await resp.json();
    return data.data;
  } catch (err) {
    console.log('getDataFromToken Error', err);
    removeCookie("token");
    return null;
  }
}

async function verify() {
  try {
    const token = getCookie("token");
    if (!token) return null;

    const respVerify = await fetch("/api/jwt/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ token })
    });

    const payload = await respVerify.json();

    const resp = await fetch("/api/me", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ id: payload.data })
    });

    const data = await resp.json();
    return data.data;
  } catch (err) {
    console.log('Verify Error', err);
    return null;
  }
}

async function checkPermissions() {
  try {
    const token = getCookie("token");
    if (!token) return null;

    const resp = await fetch("/api/permissions", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ token })
    });

    const data = await resp.json();

    return data.data;
  } catch (err) {
    console.log('Check Permissions Error', err);
    return null;
  }
}

function setTokenCookie(token: string | null) {
  if (token) {
    setCookie('token', token)
  } else {
    removeCookie('token');
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<any>(null)
  const [loading, setLoading] = React.useState(true)
  const isAuthenticated = !!user

  const logout = React.useCallback(async () => {
    await sleep(250);

    setTokenCookie(null);
    setUser(null);
  }, [])

  const login = React.useCallback(async (user: any, token: string) => {
    await sleep(500);

    setTokenCookie(token);
    setUser(user);
  }, [])

  React.useEffect(() => {
    async function load(verify: boolean) {
      const data = await getDataFromToken();
      setUser(data);
      setLoading(false);
      if (data == null && verify == true) return null;
    }

    load(false);
  }, [])

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, user, verify, checkPermissions, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}